import React, { useState, useMemo, useRef, useEffect } from 'react';

const Tabs = ({ tabs = [], initialActiveId, activeId, onChange, className = '', children }) => {
  const defaultId = initialActiveId ?? (tabs[0] && tabs[0].id);
  const [internalActive, setInternalActive] = useState(defaultId);

  const current = activeId !== undefined ? activeId : internalActive;
  const tabIndexMap = useMemo(() => tabs.map((t) => t.id), [tabs]);

  const handleSelect = (id) => {
    if (onChange) onChange(id);
    if (activeId === undefined) setInternalActive(id);
  };

  const handleKeyDown = (e, idx) => {
    const last = tabIndexMap.length - 1;
    let nextIndex = idx;
    if (e.key === 'ArrowRight') nextIndex = idx === last ? 0 : idx + 1;
    else if (e.key === 'ArrowLeft') nextIndex = idx === 0 ? last : idx - 1;
    else return;

    e.preventDefault();
    const id = tabIndexMap[nextIndex];
    handleSelect(id);
    const btn = document.querySelector(`[data-tab-btn="${id}"]`);
    if (btn) btn.focus();
  };

  const panels = React.Children.toArray(children);

  const tabsRef = useRef([]);
  const containerRef = useRef(null);
  const indicatorRef = useRef(null);

  const updateIndicator = () => {
    const idx = tabIndexMap.indexOf(current);
    if (idx === -1) {
      if (indicatorRef.current) indicatorRef.current.style.width = '0px';
      return;
    }
    const btn = tabsRef.current[idx];
    const container = containerRef.current;
    const indicator = indicatorRef.current;
    if (!btn || !container || !indicator) return;

    const btnRect = btn.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const left = btnRect.left - containerRect.left + container.scrollLeft;
    const width = btnRect.width;

    indicator.style.transform = `translateX(${left}px)`;
    indicator.style.width = `${width}px`;
  };

  useEffect(() => {
    updateIndicator();
    const onResize = () => updateIndicator();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, tabs]);

  return (
    <div className={`flex flex-col gap-2 h-full ${className}`}>
      <div
        ref={containerRef}
        role='tablist'
        aria-orientation='horizontal'
        className='relative overflow-x-auto whitespace-nowrap flex-shrink-0'
      >
        {/* Tab bar */}
        <div
          className='flex gap-1'
          style={{ borderBottom: '2px solid var(--border-subtle)' }}
        >
          {tabs.map((t, idx) => {
            const isActive = t.id === current;
            return (
              <button
                key={t.id}
                ref={(el) => (tabsRef.current[idx] = el)}
                data-tab-btn={t.id}
                role='tab'
                aria-selected={isActive}
                aria-controls={`tab-panel-${t.id}`}
                id={`tab-${t.id}`}
                onClick={() => !t.disabled && handleSelect(t.id)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                disabled={t.disabled}
                className='px-4 py-2.5 pb-3 -mb-px text-sm font-medium focus:outline-none transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40'
                style={{
                  color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = 'var(--text-muted)';
                }}
              >
                {t.title}
              </button>
            );
          })}
        </div>

        {/* Animated indicator */}
        <span
          ref={indicatorRef}
          className='absolute bottom-0 left-0 h-0.5 transition-all duration-250'
          style={{
            width: 0,
            transform: 'translateX(0px)',
            background: 'var(--accent)',
            borderRadius: '999px',
          }}
        />
      </div>

      <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
        {panels.map((p) => {
          const tabId = p.props && p.props.tabId;
          if (tabId === undefined) return null;
          const isActive = tabId === current;
          return (
            <div
              key={tabId}
              id={`tab-panel-${tabId}`}
              role='tabpanel'
              aria-labelledby={`tab-${tabId}`}
              style={isActive ? {} : { display: 'none' }}
              className='flex flex-col flex-1 h-full min-w-0'
            >
              {isActive ? p : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Tabs;
